import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    FC,
    ReactNode
} from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'light' | 'outline-light'
export type ButtonSize = 'sm' | 'md' | 'lg'

const variants: Record<ButtonVariant, string> = {
    'primary': 'bg-forest text-cream shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] hover:bg-forest-deep',
    'secondary': 'bg-sand text-charcoal hover:bg-stone',
    'outline': 'border border-charcoal/15 text-charcoal hover:border-forest hover:text-forest',
    'ghost': 'text-charcoal hover:bg-charcoal/5',
    'light': 'bg-cream text-charcoal hover:bg-sand',
    'outline-light': 'border border-cream/35 text-cream hover:border-cream hover:bg-cream hover:text-charcoal'
}

const sizes: Record<ButtonSize, string> = {
    sm: 'h-9 px-4 text-[0.8125rem]',
    md: 'h-11 px-6 text-sm',
    lg: 'h-13 px-8 text-[0.9375rem]'
}

// One source of truth for every button and button-styled link on the site
export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => cn(
    'group/button inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium tracking-[0.01em] transition-[background-color,color,border-color,transform] duration-300 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2 focus-visible:ring-offset-cream',
    'active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60',
    sizes[size],
    variants[variant],
    className
)

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
}

export const Button: FC<ButtonProps> = ({
    variant,
    size,
    className,
    type = 'button',
    ...props
}): ReactNode => {
    return <button type={type} className={buttonClasses(variant, size, className)} {...props} />
}

interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    href: string
    variant?: ButtonVariant
    size?: ButtonSize
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    variant,
    size,
    className,
    ...props
}): ReactNode => {
    return <Link href={href} className={buttonClasses(variant, size, className)} {...props} />
}
